import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import {
  ChevronLeft, Star, MessageSquare, Building2, ShieldCheck, TrendingUp,
  ThumbsUp, ThumbsDown, Sparkles, ArrowRight,
} from "lucide-react";

const SENTIMENT_COLOR = {
  positive: "bg-green-100 text-green-800",
  neutral: "bg-brand-yellow/25 text-brand-ink",
  negative: "bg-brand-orange/15 text-brand-orange",
};

export default function VendorInsights() {
  const { vendor } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/feedback/public/vendor/${vendor}`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true));
    if (typeof document !== "undefined" && vendor) {
      document.title = `${vendor} — Customer feedback | TridentDigitalPulse`;
    }
  }, [vendor]);

  if (notFound) {
    return (
      <div className="py-16 text-center" data-testid="vendor-not-found">
        <h1 className="font-heading text-3xl font-extrabold">Vendor not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Nobody has left feedback for this vendor yet.
        </p>
        <Link to="/" className="mt-6 inline-block">
          <Button className="rounded-full bg-brand-orange text-white">Back home</Button>
        </Link>
      </div>
    );
  }

  if (!data) return <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>;

  const { vendor_name, summary, ours, rating_distribution, recent_comments } = data;
  const compareData = [
    { name: vendor_name.slice(0, 22), avg: summary.avg_rating, fill: "#0A0A0A" },
    { name: "TridentDigitalPulse", avg: ours.avg, fill: "#FF3B30" },
  ];
  const winner = ours.avg > summary.avg_rating ? "TridentDigitalPulse" : vendor_name;
  const winnerDelta = Math.abs(ours.avg - summary.avg_rating).toFixed(2);

  return (
    <div className="space-y-6" data-testid="vendor-insights-page">
      <Link to="/" className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
        <ChevronLeft size={12} /> Back home
      </Link>

      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-foreground/10 bg-gradient-to-br from-brand-ink to-neutral-800 p-8 text-white sm:p-14">
        <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-brand-orange/30 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest backdrop-blur">
            <Building2 size={12} /> Customer feedback report
          </div>
          <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
            {vendor_name}
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/80">
            Real, anonymised customer feedback for <b>{vendor_name}</b>, captured through
            TridentDigitalPulse's public feedback form. No fake reviews. No paid endorsements.
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-4">
            <Kpi value={`${summary.avg_rating.toFixed(1)}★`} label="Avg rating" />
            <Kpi value={summary.count} label="Reviews" />
            <Kpi value={`${summary.positive_pct}%`} label="Positive share" accent />
            <Kpi value={summary.negative} label="Negative reviews" />
          </div>
        </div>
      </section>

      {/* Head-to-head */}
      <section className="rounded-2xl border border-foreground/10 bg-white p-6" data-testid="head-to-head">
        <div className="mb-3 flex items-center gap-2">
          <TrendingUp size={14} className="text-brand-orange" />
          <div className="font-heading text-sm font-bold uppercase tracking-widest">
            Head-to-head · {vendor_name} vs TridentDigitalPulse
          </div>
        </div>
        <div className="grid gap-4 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={compareData} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}★`} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} formatter={(v) => [`${v.toFixed(2)}★`, "Avg"]} />
                <Bar dataKey="avg" radius={[6, 6, 0, 0]}>
                  {compareData.map((r, i) => <Cell key={i} fill={r.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="lg:col-span-2 flex flex-col justify-center gap-3">
            <div className="rounded-2xl bg-brand-yellow/15 p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                {winner === "TridentDigitalPulse" ? "We lead by" : "They lead by"}
              </div>
              <div className="mt-1 font-heading text-3xl font-extrabold text-brand-orange">
                {winnerDelta}★
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                Based on <b>{summary.count + ours.count}</b> combined ratings across both platforms.
              </div>
            </div>
            {winner !== "TridentDigitalPulse" && (
              <Link to="/#feedback" className="text-xs font-semibold text-brand-orange">
                Think we should catch up? Tell us how →
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Rating distribution */}
      <section className="rounded-2xl border border-foreground/10 bg-white p-6">
        <div className="mb-3 flex items-center gap-2">
          <Star size={14} className="text-brand-orange" />
          <div className="font-heading text-sm font-bold uppercase tracking-widest">
            Rating distribution for {vendor_name}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={rating_distribution} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
            <XAxis dataKey="rating" tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}★`} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
            <Bar dataKey="count" radius={[6, 6, 0, 0]}>
              {rating_distribution.map((r, i) => (
                <Cell key={i} fill={r.rating >= 4 ? "#16a34a" : r.rating === 3 ? "#FFD600" : "#FF3B30"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </section>

      {/* Anonymised comments */}
      <section className="rounded-2xl border border-foreground/10 bg-white p-6">
        <div className="mb-3 flex items-center gap-2">
          <MessageSquare size={14} className="text-brand-orange" />
          <div className="font-heading text-sm font-bold uppercase tracking-widest">
            What customers are saying
          </div>
        </div>
        {recent_comments.length === 0 ? (
          <div className="grid h-[120px] place-items-center text-xs text-muted-foreground">
            No comments yet — be the first to review.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="vendor-comments">
            {recent_comments.map((r, i) => (
              <div key={i} className="rounded-xl border p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={12} className={n <= r.rating ? "fill-brand-yellow text-brand-yellow" : "text-muted-foreground/30"} />
                    ))}
                  </div>
                  {r.sentiment && (
                    <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest ${SENTIMENT_COLOR[r.sentiment] || "bg-muted"}`}>
                      {r.sentiment}
                    </span>
                  )}
                </div>
                <p className="mt-2 text-sm leading-relaxed">"{r.comment}"</p>
                <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                  <span>{(r.created_at || "").slice(0, 10)}</span>
                  {r.location && <span>· {r.location}</span>}
                  {r.subject_type && <span>· {r.subject_type}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="rounded-3xl bg-brand-orange p-8 text-center text-white sm:p-12">
        <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15">
          <Sparkles size={22} />
        </div>
        <h2 className="mt-4 font-heading text-2xl font-extrabold sm:text-3xl">
          Had a different experience with {vendor_name}?
        </h2>
        <p className="mx-auto mt-2 max-w-lg text-sm text-white/90">
          Every review sharpens the picture. Take 60 seconds to share yours — anonymously.
        </p>
        <Link to="/#feedback" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-brand-orange">
          Share feedback <ArrowRight size={14} />
        </Link>
      </section>

      <div className="flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
        <ShieldCheck size={12} /> All customer names anonymised · powered by AI sentiment classification
      </div>
    </div>
  );
}

function Kpi({ value, label, accent }) {
  return (
    <div className="rounded-xl bg-white/10 p-3 backdrop-blur">
      <div className={`font-heading text-2xl font-extrabold ${accent ? "text-brand-yellow" : ""}`}>{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-white/70">{label}</div>
    </div>
  );
}
